import React, { useState, useEffect, useCallback, useRef } from "react";
import {
	Box,
	Typography,
	Paper,
	Grid,
	CircularProgress,
	Alert,
	Button,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip as MuiTooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	LineElement,
	PointElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getApiBaseUrl, isDevBranchEnvironment } from "../../utils/apiConfig";
import { apiClient } from "../../services/api";

// Register ChartJS components
ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	LineElement,
	PointElement,
	ChartDataLabels,
);

// MAN brand colors matching the uploaded image
const STAGE_COLORS = {
	WF810: "#8ac926", // Green (highest bar)
	WF700: "#1e2832", // Dark blue/gray
	WF110: "#cc0000", // Red
	WF500: "#ffa500", // Yellow
	NOTR: "#333333", // Dark gray
	WF200: "#ff6b6b", // Light red
	WF100: "#4ecdc4", // Teal
	WF600: "#45b7d1", // Blue
	WF300: "#96ceb4", // Light green
	WF310: "#96ceb4", // Light green (same as WF300)
	WF320: "#ffeaa7", // Light yellow
	WF510: "#ffa500", // Yellow (same as WF500)
	WF520: "#ffa500", // Yellow (same as WF500)
	WF400: "#a29bfe", // Purple
	WF530: "#fd79a8", // Pink
	WF800: "#6c5ce7", // Dark purple
};

// Workflow stage mapping to descriptive names
const STAGE_MAPPING = {
	WF810: "Neuteile gesamt",
	WF110: "ALS",
	WF200: "Beschaffung",
	WF300: "Qualität",
	WF310: "Qualität",
	WF320: "Qualität",
	WF400: "Qualität",
	WF600: "Vorseriendisposition",
	WF700: "ALS",
};

// Display-only label overrides (does NOT affect any internal logic, redirects or calculations)
const STAGE_DISPLAY_NAMES = {
	"Neuteile gesamt": "New Parts",
	Beschaffung: "Purchasing",
	Qualität: "Quality",
	Vorseriendisposition: "Pre-series Disposition",
	"alle MST abgearbeitet": "PET Status",
};

// Define the specific order for the chart
const STAGE_ORDER = [
	"Neuteile gesamt", // WF810
	"ALS", // WF110 + WF700 (combined)
	"Beschaffung", // WF200
	"Qualität", // WF300+WF310+WF320+WF400 (combined)
	"Vorseriendisposition", // WF600
	"alle MST abgearbeitet", // Combined WF800 + WF810 + Total Summary
];

const STATUS_COLORS = {
	NO: "#cc0000", // Red - keine Erstmuster
	KT: "#5b6f85", // Grey- keine Erstmuster freigegebene
	HT: "#1e2832", // Dark Blue - keine Vertriebsfreigabe
};

// Reusable searchable multi-select dropdown for dashboard filters
const SearchableMultiSelect = ({
	label,
	options,
	value,
	onChange,
	minWidth = 120,
}) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const containerRef = useRef(null);

	const filtered = options.filter((o) =>
		o.toString().toLowerCase().includes(search.toLowerCase()),
	);

	// Close on outside click
	useEffect(() => {
		if (!open) return;
		const handler = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				setOpen(false);
				setSearch("");
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [open]);
	const allFilteredSelected =
		filtered.length > 0 && filtered.every((o) => value.includes(o));
	const someFilteredSelected =
		filtered.some((o) => value.includes(o)) && !allFilteredSelected;

	const toggleOption = (opt) => {
		if (value.includes(opt)) {
			onChange(value.filter((v) => v !== opt));
		} else {
			onChange([...value, opt]);
		}
	};

	const toggleSelectAll = (e) => {
		e.stopPropagation();
		if (allFilteredSelected) {
			// Deselect all currently filtered options
			onChange(value.filter((v) => !filtered.includes(v)));
		} else {
			// Select all currently filtered options (merge with already selected)
			const merged = [...new Set([...value, ...filtered])];
			onChange(merged);
		}
	};

	const clearAll = (e) => {
		e.stopPropagation();
		onChange([]);
	};

	const containerStyle = {
		position: "relative",
		minWidth,
		flexShrink: 0,
		fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
	};
	const triggerStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "0 8px",
		height: "32px",
		border: `1.5px solid ${open ? "#1e2832" : "#c4c4c4"}`,
		borderRadius: "4px",
		cursor: "pointer",
		backgroundColor: "white",
		fontSize: "0.75rem",
		gap: "4px",
		userSelect: "none",
		transition: "border-color 0.15s",
	};
	const panelStyle = {
		position: "absolute",
		top: "calc(100% + 4px)",
		left: 0,
		zIndex: 9999,
		backgroundColor: "white",
		border: "1.5px solid #1e2832",
		borderRadius: "4px",
		boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
		minWidth: "100%",
		width: "max-content",
		maxWidth: "280px",
	};
	const searchStyle = {
		width: "100%",
		boxSizing: "border-box",
		padding: "6px 8px",
		borderBottom: "1px solid #e0e0e0",
		border: "none",
		borderBottomWidth: "1px",
		borderBottomStyle: "solid",
		borderBottomColor: "#e0e0e0",
		fontSize: "0.75rem",
		fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		outline: "none",
	};
	const listStyle = {
		maxHeight: "220px",
		overflowY: "auto",
		padding: "4px 0",
	};
	const itemStyle = (selected) => ({
		display: "flex",
		alignItems: "center",
		gap: "8px",
		padding: "5px 10px",
		fontSize: "0.75rem",
		cursor: "pointer",
		backgroundColor: selected ? "#f0f4f8" : "transparent",
		fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
	});

	return (
		<div ref={containerRef} style={containerStyle}>
			<div
				style={triggerStyle}
				onClick={() => {
					setOpen((o) => !o);
					setSearch("");
				}}
			>
				<span
					style={{
						color: value.length > 0 ? "#1e2832" : "#888",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{value.length > 0 ? `${value.length} selected` : label}
				</span>
				<span
					style={{
						display: "flex",
						alignItems: "center",
						gap: "2px",
						flexShrink: 0,
					}}
				>
					{value.length > 0 && (
						<span
							onClick={clearAll}
							title="Clear all"
							style={{
								color: "#cc0000",
								fontWeight: "bold",
								fontSize: "13px",
								lineHeight: 1,
								padding: "0 2px",
								cursor: "pointer",
							}}
						>
							✕
						</span>
					)}
					<span style={{ color: "#666", fontSize: "10px" }}>
						{open ? "▲" : "▼"}
					</span>
				</span>
			</div>
			{open && (
				<div style={panelStyle}>
					{" "}
					<input
						autoFocus
						style={searchStyle}
						placeholder={`Search ${label}…`}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onClick={(e) => e.stopPropagation()}
					/>
					{filtered.length > 0 && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								padding: "5px 10px",
								borderBottom: "1px solid #e0e0e0",
								fontSize: "0.72rem",
								fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
								cursor: "pointer",
								backgroundColor: "#f8f8f8",
								userSelect: "none",
							}}
							onClick={toggleSelectAll}
						>
							<input
								type="checkbox"
								readOnly
								checked={allFilteredSelected}
								ref={(el) => {
									if (el) el.indeterminate = someFilteredSelected;
								}}
								style={{
									accentColor: "#cc0000",
									cursor: "pointer",
									flexShrink: 0,
								}}
							/>
							<span style={{ color: "#1e2832" }}>
								{allFilteredSelected ? "Deselect all" : "Select all"}
								{search
									? ` (${filtered.length} shown)`
									: ` (${filtered.length})`}
							</span>
						</div>
					)}
					<div style={listStyle}>
						{filtered.length === 0 ? (
							<div
								style={{
									padding: "8px 10px",
									fontSize: "0.72rem",
									color: "#999",
								}}
							>
								No options found
							</div>
						) : (
							filtered.map((opt) => {
								const selected = value.includes(opt);
								return (
									<div
										key={opt}
										style={itemStyle(selected)}
										onMouseEnter={(e) =>
											(e.currentTarget.style.backgroundColor = "#f5f5f5")
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.backgroundColor = selected
												? "#f0f4f8"
												: "transparent")
										}
										onClick={() => toggleOption(opt)}
									>
										<input
											type="checkbox"
											readOnly
											checked={selected}
											style={{
												accentColor: "#cc0000",
												cursor: "pointer",
												flexShrink: 0,
											}}
										/>
										<span
											style={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{opt}
										</span>
									</div>
								);
							})
						)}
					</div>
					{value.length > 0 && (
						<div
							style={{ borderTop: "1px solid #e0e0e0", padding: "5px 10px" }}
						>
							<span
								onClick={clearAll}
								style={{
									fontSize: "0.72rem",
									color: "#cc0000",
									cursor: "pointer",
									fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
								}}
							>
								Clear all ({value.length})
							</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

const SeriesDashboard = ({ onFloatingStateChange }) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [seriesData, setSeriesData] = useState([]);
	const [chartData, setChartData] = useState(null);
	const [summaryStats, setSummaryStats] = useState({
		totalParts: 0,
		stageBreakdown: {},
		statusBreakdown: {},
		chartStages: [], // Add chart stages to track what's actually displayed
	});
	const [filterOptions, setFilterOptions] = useState({
		projectNumbers: [],
		algValues: [],
		changeNumbers: [],
		projectAlgMapping: {},
		projectChangeNoMapping: {},
		algPetMapping: {},
		projectAlgChangePetMapping: {},
	});
	const [selectedFilters, setSelectedFilters] = useState({
		projectNumber: [],
		alg: [],
		changeNumber: [],
	});

	const [appliedFilters, setAppliedFilters] = useState({
		projectNumber: [],
		alg: [],
		changeNumber: [],
	});

	const [showPet, setShowPet] = useState(true);

	// Floating Dashboard toggle. When enabled, the same waterfall chart is driven
	// by /api/floating-dashboard-data (routed through the shared chartData /
	// summaryStats / loading / error state so the design is byte-for-byte identical).
	const [floatingMode, setFloatingMode] = useState(false);

	// Which day's snapshot the floating dashboard is currently showing.
	// shownDate is a "YYYY-MM-DD" string; isCurrentDay is false when today's
	// snapshot isn't available yet and we've fallen back to the most recent day.
	const [floatingDataInfo, setFloatingDataInfo] = useState({
		shownDate: null,
		isCurrentDay: true,
	});

	// "Future FET" modal — shows rows from floating_future_no_ident_dev that
	// match the current floating filter selection (project / ALG / change no).
	const [futureModalOpen, setFutureModalOpen] = useState(false);
	const [futureLoading, setFutureLoading] = useState(false);
	const [futureError, setFutureError] = useState(null);
	const [futureData, setFutureData] = useState([]);
	const [futureInfo, setFutureInfo] = useState({
		shownDate: null,
		isCurrentDay: true,
	});

	// Floating Dashboard has its own filter set sourced from the
	// floating_dashboard_data table (project = projektnummer, change no = aendnr).
	// The source table has no ALG column, so only these two filters exist.
	const [floatingFilterOptions, setFloatingFilterOptions] = useState({
		projectNumbers: [],
		algValues: [],
		changeNumbers: [],
		projectAlgMapping: {},
		projectChangeNoMapping: {},
		algChangeNoMapping: {},
	});
	const [floatingSelectedFilters, setFloatingSelectedFilters] = useState({
		projectNumber: [],
		alg: [],
		changeNumber: [],
	});
	const [floatingAppliedFilters, setFloatingAppliedFilters] = useState({
		projectNumber: [],
		alg: [],
		changeNumber: [],
	});

	// Notify the parent (DashboardPage) of the current floating mode + applied
	// filters so it can render the "Export Data" button next to "Export to PDF".
	useEffect(() => {
		if (typeof onFloatingStateChange === "function") {
			onFloatingStateChange({
				floatingMode,
				projectNumber: floatingAppliedFilters.projectNumber,
				alg: floatingAppliedFilters.alg,
				changeNumber: floatingAppliedFilters.changeNumber,
			});
		}
	}, [onFloatingStateChange, floatingMode, floatingAppliedFilters]);

	const chartRef = useRef(null); // Fetch filter options from backend
	const fetchFilterOptions = useCallback(async () => {
		try {
			const response = await apiClient.get(
				"/api/series-dashboard-filter-options",
			);
			setFilterOptions({
				...response.data,
				algPetMapping: response.data.algPetMapping || {},
				projectAlgChangePetMapping:
					response.data.projectAlgChangePetMapping || {},
			});
		} catch (err) {
			console.error("Error fetching filter options:", err);
			if (err.response?.status === 401) {
				setError("Authentication required. Please log in again.");
			} else {
				setError("Failed to fetch filter options");
			}
		}
	}, []);

	// Fetch filter options for the Floating Dashboard from the dedicated endpoint
	// (distinct project / change-number values in floating_dashboard_data).
	const fetchFloatingFilterOptions = useCallback(async () => {
		try {
			const response = await apiClient.get(
				"/api/floating-dashboard-filter-options",
			);
			setFloatingFilterOptions({
				projectNumbers: response.data.projectNumbers || [],
				algValues: response.data.algValues || [],
				changeNumbers: response.data.changeNumbers || [],
				projectAlgMapping: response.data.projectAlgMapping || {},
				projectChangeNoMapping: response.data.projectChangeNoMapping || {},
				algChangeNoMapping: response.data.algChangeNoMapping || {},
			});
		} catch (err) {
			console.error("Error fetching floating filter options:", err);
		}
	}, []);

	// Fetch series data from backend
	const fetchSeriesData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			// Build query parameters using applied filters
			const params = new URLSearchParams();

			// For multiple project numbers, append each as a separate parameter
			if (appliedFilters.projectNumber.length > 0) {
				appliedFilters.projectNumber.forEach((project) => {
					params.append("project_number", project);
				});
			}
			// For multiple ALG values, append each as a separate parameter
			if (appliedFilters.alg.length > 0) {
				appliedFilters.alg.forEach((alg) => {
					params.append("alg", alg);
				});
			}

			// For multiple change numbers, append each as a separate parameter
			if (appliedFilters.changeNumber.length > 0) {
				appliedFilters.changeNumber.forEach((cn) => {
					params.append("change_no", cn);
				});
			}
			const url = `/api/series-dashboard-data${params.toString() ? "?" + params.toString() : ""}`;
			console.log("Fetching data with URL:", url); // Debug log
			const response = await apiClient.get(url);
			const aggregatedData = response.data.data || [];
			const qiData = response.data.qi_data || [];
			const sfData = response.data.sf_data || [];
			setSeriesData(aggregatedData);
			processSeriesData(aggregatedData, qiData, sfData);
			setLoading(false);
		} catch (err) {
			console.error("Error fetching series data:", err);
			if (err.response?.status === 401) {
				setError("Authentication required. Please log in again.");
			} else {
				setError(err.message || "Failed to fetch series data");
			}
			setLoading(false);
		}
	}, [appliedFilters]);

	// Process series data for waterfall chart  // Process aggregated series data for waterfall chart.
	// `data` is now an array of { wf_stufe, wf_bza, count } objects returned by the
	// server-side aggregation endpoint — NOT raw rows — to avoid 413 payload errors.
	const processSeriesData = useCallback((data, qiData = [], sfData = []) => {
		if (!data || data.length === 0) {
			setError("No series data available");
			setChartData(null);
			return;
		}

		const EXCLUDED_STAGES = new Set([
			"WF100", // Technik / Engineering — no longer tracked on the dashboard
			"WF500",
			"WF510",
			"WF520",
			"WF530",
			"NOTR",
		]);

		// Build lookup: { wf_stufe: { wf_bza: count } }
		const stageBreakdown = {}; // keyed by raw wf_stufe
		const statusBreakdown = {}; // keyed by wf_bza
		const mappedStageBreakdown = {}; // keyed by display name

		data.forEach((item) => {
			const stage = item.wf_stufe || "Unknown";
			const status = item.wf_bza || "Unknown";
			const count = Number(item.count) || 0;

			// Server already excluded NOTR rows, but guard here too
			if (status === "NO" || EXCLUDED_STAGES.has(stage)) return;

			const mappedStage = STAGE_MAPPING[stage] || stage;

			stageBreakdown[stage] = (stageBreakdown[stage] || 0) + count;
			mappedStageBreakdown[mappedStage] =
				(mappedStageBreakdown[mappedStage] || 0) + count;
			statusBreakdown[status] = (statusBreakdown[status] || 0) + count;
		});

		// Helper: count for a specific (mappedStage, status) pair
		const getCountForMappedStageAndStatus = (mappedStage, status) => {
			let total = 0;
			data.forEach((item) => {
				if (EXCLUDED_STAGES.has(item.wf_stufe)) return;
				if (item.wf_bza === "NO") return;
				const ms = STAGE_MAPPING[item.wf_stufe] || item.wf_stufe;
				if (ms === mappedStage && item.wf_bza === status) {
					total += Number(item.count) || 0;
				}
			});
			return total;
		};

		// Helper: total count for a status across ALL stages (for Neuteile gesamt bar)
		const getTotalCountForStatus = (status) => {
			let total = 0;
			data.forEach((item) => {
				if (EXCLUDED_STAGES.has(item.wf_stufe)) return;
				if (item.wf_bza === status) total += Number(item.count) || 0;
			});
			return total;
		};

		const totalCount = Object.values(stageBreakdown).reduce((s, v) => s + v, 0);
		const wf800Count = stageBreakdown["WF800"] || 0;
		const wf810Count = stageBreakdown["WF810"] || 0;

		const orderedStages = STAGE_ORDER;

		const intermediateStagesTotal = orderedStages
			.filter((s) => s !== "Neuteile gesamt" && s !== "alle MST abgearbeitet")
			.reduce((sum, s) => sum + (mappedStageBreakdown[s] || 0), 0);

		// Unique statuses (excluding NO), sorted
		const allStatuses = [...new Set(data.map((i) => i.wf_bza))]
			.filter((s) => s !== "NO")
			.sort();

		// Per-stage total values for the waterfall base calculation
		const stageValues = orderedStages.map((stage) => {
			if (stage === "alle MST abgearbeitet") return totalCount;
			return mappedStageBreakdown[stage] || 0;
		});

		const datasets = [];

		// Invisible base dataset (waterfall offset)
		const baseValues = [];
		let previousTotal = 0;
		stageValues.forEach((value, index) => {
			if (index === 0) {
				baseValues.push(0);
			} else if (orderedStages[index] === "alle MST abgearbeitet") {
				baseValues.push(0);
			} else {
				baseValues.push(previousTotal);
			}
			if (orderedStages[index] !== "alle MST abgearbeitet")
				previousTotal += value;
		});

		datasets.push({
			label: "Base",
			data: baseValues,
			backgroundColor: "rgba(0,0,0,0)",
			borderColor: "rgba(0,0,0,0)",
			borderWidth: 0,
			stack: "waterfall",
			order: 1,
		});

		// Status datasets (KT / HT)
		allStatuses.forEach((status, statusIndex) => {
			const statusData = orderedStages.map((stage) => {
				if (stage === "alle MST abgearbeitet") return 0;
				if (stage === "Neuteile gesamt") return getTotalCountForStatus(status);
				return getCountForMappedStageAndStatus(stage, status);
			});

			const statusLabel =
				status === "KT" ? "Kaufteil" : status === "HT" ? "Hausteil" : status;

			const backgroundColor = orderedStages.map((stage) =>
				stage === "Neuteile gesamt"
					? STATUS_COLORS[status] || "#6c757d"
					: "#808080",
			);
			const borderColor = backgroundColor;

			datasets.push({
				label: statusLabel,
				data: statusData,
				backgroundColor,
				borderColor,
				borderWidth: 1,
				stack: "waterfall",
				order: statusIndex + 2,
				barThickness: 80,
				maxBarThickness: 120,
			});
		});

		// Summary bar datasets
		// PET Versorgung OK now includes both WF810 (originally OK) and WF800
		// (previously shown separately as "PET Versorgung NOK"). The NOK segment
		// is no longer displayed on the Series Dashboard.
		datasets.push({
			label: "PET Versorgung OK",
			data: orderedStages.map((s) =>
				s === "alle MST abgearbeitet" ? wf810Count + wf800Count : 0,
			),
			backgroundColor: "#91b900",
			borderColor: "#91b900",
			borderWidth: 1,
			stack: "waterfall",
			order: allStatuses.length + 2,
			barThickness: 80,
			maxBarThickness: 120,
		});

		datasets.push({
			label: "No PET",
			data: orderedStages.map((s) =>
				s === "alle MST abgearbeitet" ? intermediateStagesTotal : 0,
			),
			backgroundColor: "#808080",
			borderColor: "#808080",
			borderWidth: 1,
			stack: "waterfall",
			order: allStatuses.length + 4,
			barThickness: 80,
			maxBarThickness: 120,
		});
		setChartData({ labels: orderedStages, datasets });

		// Build chartStageBreakdown for summary cards & tooltips
		const chartStageBreakdown = {};
		orderedStages.forEach((stage) => {
			if (stage === "Neuteile gesamt" || stage === "alle MST abgearbeitet") {
				chartStageBreakdown[stage] = totalCount;
			} else {
				chartStageBreakdown[stage] = mappedStageBreakdown[stage] || 0;
			}
		});

		// Build QI status breakdown: { displayStage: { qi100: {status: count}, qi500: {...}, qi900: {...} } }
		const qiStageBreakdown = {};
		if (qiData && qiData.length > 0) {
			qiData.forEach((item) => {
				const rawStage = item.wf_stufe || "Unknown";
				if (EXCLUDED_STAGES.has(rawStage)) return;
				if (item.wf_bza === "NO") return;
				const mappedStage = STAGE_MAPPING[rawStage] || rawStage;
				const count = Number(item.count) || 0;
				if (!qiStageBreakdown[mappedStage]) {
					qiStageBreakdown[mappedStage] = { qi100: {}, qi500: {}, qi900: {} };
				}
				const q1 = item.qi100_status || "N/A";
				const q5 = item.qi500_status || "N/A";
				const q9 = item.qi900_status || "N/A";
				const q1Nok = q1.toUpperCase().includes("NOK");
				const q5Nok = q5.toUpperCase().includes("NOK");
				qiStageBreakdown[mappedStage].qi100[q1] =
					(qiStageBreakdown[mappedStage].qi100[q1] || 0) + count;
				// Only count QI500 if QI100 is not NOK
				if (!q1Nok) {
					qiStageBreakdown[mappedStage].qi500[q5] =
						(qiStageBreakdown[mappedStage].qi500[q5] || 0) + count;
				}
				// Only count QI900 if neither QI100 nor QI500 is NOK
				if (!q1Nok && !q5Nok) {
					qiStageBreakdown[mappedStage].qi900[q9] =
						(qiStageBreakdown[mappedStage].qi900[q9] || 0) + count;
				}
			});
		}

		// Build SF (sofrei_ende >= today) breakdown per display stage.
		// Used by the Quality and Pre-series Disposition tooltips.
		const sfStageBreakdown = {};
		if (sfData && sfData.length > 0) {
			sfData.forEach((item) => {
				const rawStage = item.wf_stufe || "Unknown";
				if (EXCLUDED_STAGES.has(rawStage)) return;
				if (item.wf_bza === "NO") return;
				const mappedStage = STAGE_MAPPING[rawStage] || rawStage;
				const count = Number(item.count) || 0;
				sfStageBreakdown[mappedStage] =
					(sfStageBreakdown[mappedStage] || 0) + count;
			});
		}

		setSummaryStats({
			totalParts: totalCount,
			stageBreakdown: chartStageBreakdown,
			statusBreakdown,
			chartStages: orderedStages,
			qiStageBreakdown,
			sfStageBreakdown,
		});
	}, []);

	// Fetch floating-dashboard data and render it through the SAME waterfall
	// pipeline as the main chart. The backend returns compact {stage, count} rows
	// for stages ALS / Beschaffung / Qualität / Vorseriendisposition / "alle MST
	// abgearbeitet". We translate those into the {wf_stufe, wf_bza, count} row
	// shape that processSeriesData expects, so the resulting chart is structurally
	// identical (waterfall offset, red division lines, grand-total boxes, "Parts
	// in progress" band, PET OK / No PET summary bar).
	//
	// Only project + change_no filters apply — the source table has no ALG column.
	const fetchFloatingData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const params = new URLSearchParams();
			if (floatingAppliedFilters.projectNumber.length > 0) {
				floatingAppliedFilters.projectNumber.forEach((project) => {
					params.append("project_number", project);
				});
			}
			if (floatingAppliedFilters.alg.length > 0) {
				floatingAppliedFilters.alg.forEach((a) => {
					params.append("alg", a);
				});
			}
			if (floatingAppliedFilters.changeNumber.length > 0) {
				floatingAppliedFilters.changeNumber.forEach((cn) => {
					params.append("change_no", cn);
				});
			}

			const url = `/api/floating-dashboard-data${params.toString() ? "?" + params.toString() : ""}`;
			const response = await apiClient.get(url);
			const rows = response.data.data || [];

			// Track which daily snapshot the backend returned (today, or the most
			// recent available day when today's data isn't loaded yet).
			setFloatingDataInfo({
				shownDate: response.data.shown_date || null,
				isCurrentDay: response.data.is_current_day !== false,
			});

			// Map each floating milestone stage to a representative wf_stufe code so
			// STAGE_MAPPING routes it to the correct display stage. "alle MST
			// abgearbeitet" (done) → WF800 so it lands in the green "PET Versorgung
			// OK" segment of the final bar (matching the series waterfall).
			const STAGE_TO_SYNTHETIC_STUFE = {
				ALS: "WF110",
				Beschaffung: "WF200",
				Qualität: "WF300",
				Vorseriendisposition: "WF600",
				"alle MST abgearbeitet": "WF800",
			};

			const syntheticRows = [];
			rows.forEach((item) => {
				const stufe = STAGE_TO_SYNTHETIC_STUFE[item.stage];
				const count = Number(item.count) || 0;
				if (stufe && count > 0) {
					// Single neutral status series (floating data has no KT/HT split).
					syntheticRows.push({ wf_stufe: stufe, wf_bza: "Parts", count });
				}
			});

			setSeriesData(syntheticRows);
			processSeriesData(syntheticRows, [], []);
			setLoading(false);
		} catch (err) {
			console.error("Error fetching floating dashboard data:", err);
			if (err.response?.status === 401) {
				setError("Authentication required. Please log in again.");
			} else {
				setError(err.message || "Failed to fetch floating dashboard data");
			}
			setLoading(false);
		}
	}, [floatingAppliedFilters, processSeriesData]);

	// Fetch the future-parts detail rows (floating_future_no_ident_dev) for the
	// modal, using the same project / ALG / change-number selection currently
	// applied to the floating dashboard.
	const fetchFutureData = useCallback(async () => {
		try {
			setFutureLoading(true);
			setFutureError(null);

			const params = new URLSearchParams();
			floatingAppliedFilters.projectNumber.forEach((project) =>
				params.append("project_number", project),
			);
			floatingAppliedFilters.alg.forEach((a) => params.append("alg", a));
			floatingAppliedFilters.changeNumber.forEach((cn) =>
				params.append("change_no", cn),
			);

			const url = `/api/floating-future-data${params.toString() ? "?" + params.toString() : ""}`;
			const response = await apiClient.get(url);
			setFutureData(response.data.data || []);
			setFutureInfo({
				shownDate: response.data.shown_date || null,
				isCurrentDay: response.data.is_current_day !== false,
			});
			setFutureLoading(false);
		} catch (err) {
			console.error("Error fetching Future FET data:", err);
			setFutureError(
				err.response?.status === 401
					? "Authentication required. Please log in again."
					: err.message || "Failed to fetch Future FET data",
			);
			setFutureLoading(false);
		}
	}, [floatingAppliedFilters]);

	// Open the future-parts modal and (re)load its data.
	const openFutureModal = useCallback(() => {
		setFutureModalOpen(true);
		fetchFutureData();
	}, [fetchFutureData]);

	// Refetch floating data whenever it's enabled or the floating applied filters change
	useEffect(() => {
		if (floatingMode) {
			fetchFloatingData();
		}
	}, [floatingMode, fetchFloatingData]);

	// Load floating filter options the first time floating mode is enabled
	useEffect(() => {
		if (floatingMode) {
			fetchFloatingFilterOptions();
		}
	}, [floatingMode, fetchFloatingFilterOptions]);

	// Custom plugin to draw red division lines and section label
	const redDivisionLinesPlugin = {
		id: "redDivisionLines",
		beforeDatasetsDraw: (chart) => {
			const ctx = chart.ctx;
			const chartArea = chart.chartArea;
			const labels = chart.data.labels;

			// Find the positions where we need to draw red lines
			const neuteileIndex = labels.findIndex(
				(label) => label === "Neuteile gesamt",
			);
			const vorserieMstIndex = labels.findIndex(
				(label) => label === "alle MST abgearbeitet",
			);

			if (neuteileIndex >= 0 || vorserieMstIndex > 0) {
				ctx.save();
				ctx.strokeStyle = "#cc0000"; // Red color
				ctx.lineWidth = 3;
				ctx.setLineDash([]);

				// Calculate bar width and spacing
				const barWidth = (chartArea.right - chartArea.left) / labels.length;
				// Draw line after Neuteile gesamt (always show this line)
				let firstLineX = 0;
				if (neuteileIndex >= 0) {
					const x =
						chartArea.left + (neuteileIndex + 1) * barWidth - barWidth * 0.1;
					firstLineX = x;
					ctx.beginPath();
					ctx.moveTo(x, chartArea.top - 10);
					ctx.lineTo(x, chartArea.bottom + 40);
					ctx.stroke();
				}
				// Draw line between Vorseriendisposition and alle MST abgearbeitet
				let secondLineX = 0;
				if (vorserieMstIndex > 0) {
					const x =
						chartArea.left + vorserieMstIndex * barWidth - barWidth * 0.1;
					secondLineX = x;
					ctx.beginPath();
					ctx.moveTo(x, chartArea.top - 10);
					ctx.lineTo(x, chartArea.bottom + 40);
					ctx.stroke();
				}

				ctx.restore();
			}
		},
		afterDatasetsDraw: (chart) => {
			const ctx = chart.ctx;
			const chartArea = chart.chartArea;
			const labels = chart.data.labels;

			// Find the positions where we need to draw the text box
			const neuteileIndex = labels.findIndex(
				(label) => label === "Neuteile gesamt",
			);
			const vorserieMstIndex = labels.findIndex(
				(label) => label === "alle MST abgearbeitet",
			);

			if (neuteileIndex >= 0 && vorserieMstIndex > 0) {
				ctx.save();

				// Calculate bar width and spacing
				const barWidth = (chartArea.right - chartArea.left) / labels.length;

				const firstLineX =
					chartArea.left + (neuteileIndex + 1) * barWidth - barWidth * 0.1;
				const secondLineX =
					chartArea.left + vorserieMstIndex * barWidth - barWidth * 0.1;

				// Draw "Bauteile in Abarbeitung" text box between the red lines
				if (firstLineX > 0 && secondLineX > 0) {
					const textBoxY = chartArea.bottom - -15; // Position 40px above chart bottom (above x-axis labels)
					const textBoxWidth = secondLineX - firstLineX;
					const textBoxHeight = 25;
					const textBoxX = firstLineX + textBoxWidth / 2;

					// Draw background box
					ctx.fillStyle = "#cc0000"; // Red background
					ctx.fillRect(firstLineX, textBoxY, textBoxWidth, textBoxHeight);

					// Draw border
					ctx.strokeStyle = "#990000"; // Darker red border
					ctx.lineWidth = 1;
					ctx.strokeRect(firstLineX, textBoxY, textBoxWidth, textBoxHeight);

					// Draw text
					ctx.fillStyle = "white";
					ctx.font = "14px MAN EUROPE_Bold";
					ctx.textAlign = "center";
					ctx.textBaseline = "middle";
					ctx.fillText(
						"Parts in progress",
						textBoxX,
						textBoxY + textBoxHeight / 2,
					);
				}

				ctx.restore();
			}
		},
	};

	// Custom plugin to draw grand total labels above specific bars
	const grandTotalLabelsPlugin = {
		id: "grandTotalLabels",
		afterDatasetsDraw: (chart) => {
			const ctx = chart.ctx;
			const chartArea = chart.chartArea;
			const labels = chart.data.labels;
			const datasets = chart.data.datasets;

			// Find the positions where we need to draw grand totals
			const neuteileIndex = labels.findIndex(
				(label) => label === "Neuteile gesamt",
			);
			const alleMstIndex = labels.findIndex(
				(label) => label === "alle MST abgearbeitet",
			);

			ctx.save();
			ctx.font = "15px MAN EUROPE_Bold";
			ctx.textAlign = "center";
			ctx.textBaseline = "bottom";

			// Calculate bar width and spacing
			const barWidth = (chartArea.right - chartArea.left) / labels.length;

			// Function to calculate grand total for a specific bar index
			const calculateGrandTotal = (barIndex) => {
				let total = 0;
				datasets.forEach((dataset) => {
					if (dataset.label !== "Base" && dataset.data[barIndex] > 0) {
						total += dataset.data[barIndex];
					}
				});
				return total;
			};

			// Function to get the top Y position of a bar
			const getBarTopY = (barIndex) => {
				let maxY = chartArea.bottom;
				datasets.forEach((dataset) => {
					if (dataset.label !== "Base" && dataset.data[barIndex] > 0) {
						const meta = chart.getDatasetMeta(datasets.indexOf(dataset));
						if (meta.data[barIndex]) {
							const barY = meta.data[barIndex].y;
							if (barY < maxY) {
								maxY = barY;
							}
						}
					}
				});
				return maxY;
			};

			// Draw grand total for Neuteile gesamt
			if (neuteileIndex >= 0) {
				const grandTotal = calculateGrandTotal(neuteileIndex);
				const x = chartArea.left + (neuteileIndex + 0.5) * barWidth;
				const y = getBarTopY(neuteileIndex) - 25;
				//© yasshsakhare
				// Draw background box
				const text = grandTotal.toLocaleString();
				const textMetrics = ctx.measureText(text);
				const boxWidth = textMetrics.width + 16;
				const boxHeight = 20;

				ctx.fillStyle = "white";
				ctx.strokeStyle = "black";
				ctx.lineWidth = 1;
				ctx.fillRect(x - boxWidth / 2, y - boxHeight, boxWidth, boxHeight);
				ctx.strokeRect(x - boxWidth / 2, y - boxHeight, boxWidth, boxHeight);

				// Draw text
				ctx.fillStyle = "black";
				ctx.fillText(text, x, y - 3);
			}

			// Draw grand total for alle MST abgearbeitet
			if (alleMstIndex >= 0) {
				const grandTotal = calculateGrandTotal(alleMstIndex);
				const x = chartArea.left + (alleMstIndex + 0.5) * barWidth;
				const y = getBarTopY(alleMstIndex) - 25;

				// Draw background box
				const text = grandTotal.toLocaleString();
				const textMetrics = ctx.measureText(text);
				const boxWidth = textMetrics.width + 16;
				const boxHeight = 20;

				ctx.fillStyle = "white";
				ctx.strokeStyle = "black";
				ctx.lineWidth = 1;
				ctx.fillRect(x - boxWidth / 2, y - boxHeight, boxWidth, boxHeight);
				ctx.strokeRect(x - boxWidth / 2, y - boxHeight, boxWidth, boxHeight);

				// Draw text
				ctx.fillStyle = "black";
				ctx.fillText(text, x, y - 3);
			}

			ctx.restore();
		},
	};

	// Map from display stage name → raw wf_stufe codes used to filter Series Tracking
	const STAGE_TO_WF_STUFE = {
		"Neuteile gesamt": [
			"WF110",
			"WF200",
			"WF300",
			"WF310",
			"WF320",
			"WF400",
			"WF600",
			"WF700",
			"WF810",
			"WF800",
		],
		ALS: ["WF110", "WF700"],
		Beschaffung: ["WF200"],
		Qualität: ["WF300", "WF310", "WF320", "WF400"],
		Vorseriendisposition: ["WF600"],
		"alle MST abgearbeitet": ["WF800", "WF810"],
	};
	// Handle bar click → navigate to Series Tracking with matching filters.
	// Disabled in Floating mode — the floating waterfall has no Series Tracking
	// equivalent to redirect to.
	const handleChartClick = (event, activeElements) => {
		if (floatingMode) return;
		const chart = chartRef.current;
		if (!chart) return;

		// Re-query elements at the actual click point using 'nearest' + intersect:true
		// so we only fire when the user truly clicks a visible bar segment
		const nativeEvent = event?.native;
		if (!nativeEvent) return;

		const clickedElements = chart.getElementsAtEventForMode(
			nativeEvent,
			"nearest",
			{ intersect: true },
			false,
		);

		if (!clickedElements || clickedElements.length === 0) return;

		// Find the first non-Base element
		let target = null;
		for (const el of clickedElements) {
			const lbl = chart.data.datasets[el.datasetIndex]?.label;
			if (lbl && lbl !== "Base") {
				target = el;
				break;
			}
		}

		// Fall back: if all intersected are Base, try index-mode activeElements (non-Base)
		if (!target && activeElements && activeElements.length > 0) {
			for (const el of activeElements) {
				const lbl = chart.data.datasets[el.datasetIndex]?.label;
				if (lbl && lbl !== "Base") {
					target = el;
					break;
				}
			}
		}

		if (!target) return;

		const { datasetIndex, index: dataIndex } = target;
		const stageName = chart.data.labels[dataIndex];
		const datasetLabel = chart.data.datasets[datasetIndex]?.label;
		if (!datasetLabel || datasetLabel === "Base") return;
		console.log("📊 Chart clicked:", {
			stageName,
			datasetLabel,
			datasetIndex,
			dataIndex,
		});

		// Resolve the wf_stufe codes for this stage/dataset combination
		let wfStufeList;
		if (stageName === "alle MST abgearbeitet") {
			// Green "PET Versorgung OK" stack now covers WF810 + WF800.
			// Grey "No PET" = all intermediate parts (no stufe filter).
			if (datasetLabel === "PET Versorgung OK") {
				wfStufeList = ["WF810", "WF800"];
			} else {
				// Grey "No PET" bar — all intermediate workflow stages
				wfStufeList = [
					"WF110",
					"WF200",
					"WF300",
					"WF310",
					"WF320",
					"WF400",
					"WF600",
					"WF700",
				];
			}
		} else {
			wfStufeList = STAGE_TO_WF_STUFE[stageName] ?? [];
		}

		// For intermediate stages (Technik, ALS, etc.) always use both KT and HT.
		// For 'Neuteile gesamt', use the clicked stack's BZA (Kaufteil→KT, Hausteil→HT).
		// For 'alle MST abgearbeitet', always use both (handled per-dataset above).
		const INTERMEDIATE_STAGES = [
			"ALS",
			"Beschaffung",
			"Qualität",
			"Vorseriendisposition",
		];
		let wfBzaList;
		if (
			INTERMEDIATE_STAGES.includes(stageName) ||
			stageName === "alle MST abgearbeitet"
		) {
			wfBzaList = ["KT", "HT"];
		} else {
			// 'Neuteile gesamt' — respect the clicked stack
			const LABEL_TO_WF_BZA = { Kaufteil: ["KT"], Hausteil: ["HT"] };
			wfBzaList = LABEL_TO_WF_BZA[datasetLabel] ?? ["KT", "HT"];
		}

		// wf_alg should always be 'NOTR' to correctly scope the backend query.
		const wfAlgList = ["NOTR"]; // Build URL
		const urlParams = new URLSearchParams({ from_dashboard: "true" });
		urlParams.set("wf_stage", stageName);
		// Always exclude NOTR entries when navigating from the dashboard
		urlParams.set("exclude_notr", "true");
		// Exclude parts with BTT status 'deleted' — consistent with the dashboard chart
		urlParams.set("exclude_btt_deleted", "true");

		if (wfStufeList.length > 0) {
			urlParams.set("wf_stufe", wfStufeList.join(","));
		}
		if (wfBzaList.length > 0) {
			urlParams.set("wf_bza", wfBzaList.join(","));
		}
		if (wfAlgList.length > 0) {
			urlParams.set("wf_alg", wfAlgList.join(","));
		}

		// Pass through any active dashboard filters
		if (appliedFilters.projectNumber.length > 0) {
			urlParams.set("project_number", appliedFilters.projectNumber.join(","));
		}
		if (appliedFilters.alg.length > 0) {
			urlParams.set("alg", appliedFilters.alg.join(","));
		}
		if (appliedFilters.changeNumber.length > 0) {
			urlParams.set("change_no", appliedFilters.changeNumber.join(","));
		}

		const targetUrl = `${window.location.origin}/series-tracking?${urlParams.toString()}`;
		console.log("📊 Dashboard click-through →", targetUrl);
		window.open(targetUrl, "_blank");
	};

	// Chart options for waterfall-style presentation
	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		layout: {
			padding: {
				top: 60,
				bottom: 60,
				left: 20,
				right: 40,
			},
		},
		plugins: {
			legend: {
				position: "bottom",
				align: "start",
				labels: {
					font: {
						family: "MAN EUROPE_Bold",
						size: 12,
					},
					usePointStyle: true,
					padding: 20,
					boxWidth: 12,
					filter: function (legendItem, chartData) {
						// Hide the base (waterfall offset) dataset from the legend
						return legendItem.text !== "Base";
					},
				},
				onHover: (e) => {
					e.native.target.style.cursor = "pointer";
				},
				onLeave: (e) => {
					e.native.target.style.cursor = "default";
				},
			},
			tooltip: {
				enabled: true, // Enable default Chart.js tooltip
				mode: "index",
				intersect: false,
				backgroundColor: "rgba(255, 255, 255, 0.95)",
				titleColor: "#1e2832",
				bodyColor: "#1e2832",
				borderColor: "#cc0000",
				borderWidth: 1,
				titleFont: {
					family: "MAN EUROPE_Bold",
					size: 13,
				},
				bodyFont: {
					family: "MAN EUROPE_Regular",
					size: 12,
				},
				footerColor: "black",
				footerFont: {
					family: "MAN EUROPE_Regular",
					size: 12,
				},
				footerMarginTop: 8,
				displayColors: true,
				filter: function (tooltipItem) {
					// For stages with zero values, still show the tooltip but filter out datasets with 0 values
					// This allows the stage to be visible but doesn't clutter tooltip with empty entries
					if (!tooltipItem || !tooltipItem.parsed) return false;
					return tooltipItem.parsed.y > 0;
				},
				callbacks: {
					title: (context) => {
						if (!context || !Array.isArray(context) || context.length === 0) {
							return "Workflow Stage";
						}
						const stage = context[0]?.label;
						if (stage === "alle MST abgearbeitet") {
							return "Alle MST abgearbeitet - Total Overview";
						}
						return `Workflow Stage: ${stage || "Unknown"}`;
					},
					label: (context) => {
						if (!context || !context.dataset) return "";
						if (context.dataset.label === "Base") return "";

						const stage = context.label;
						const statusLabel = context.dataset.label;
						const count = context.parsed?.y || 0;

						// Only show non-zero values
						if (count === 0) return "";

						if (stage === "alle MST abgearbeitet") {
							if (statusLabel === "PET Versorgung OK") {
								return `PET Versorgung OK: ${count} parts`;
							} else if (statusLabel === "No PET") {
								// Calculate intermediate stages total from the stage breakdown (should match chart values)
								const intermediateTotal = Object.entries(
									summaryStats.stageBreakdown,
								)
									.filter(
										([stageName]) =>
											stageName !== "Neuteile gesamt" &&
											stageName !== "alle MST abgearbeitet",
									)
									.reduce((sum, [, stageCount]) => sum + stageCount, 0);
								return `NO PET: ${intermediateTotal} parts (sum of intermediate workflow stages)`;
							}
						}

						return `${statusLabel}: ${count} parts`;
					},
					afterBody: (context) => {
						if (!context || !Array.isArray(context) || context.length === 0) {
							return [];
						}
						const stage = context[0]?.label;
						// All counts now come from summaryStats.stageBreakdown (aggregated server-side)
						const grandTotal = summaryStats.totalParts || 0;

						if (stage === "alle MST abgearbeitet") {
							return [`Total for ${stage}: ${grandTotal} parts`];
						}

						if (stage === "Neuteile gesamt") {
							const neuteileTotal =
								summaryStats.stageBreakdown["Neuteile gesamt"] || 0;
							const percentage =
								grandTotal > 0
									? ((neuteileTotal / grandTotal) * 100).toFixed(1)
									: "0.0";
							return [
								`Total for ${stage}: ${neuteileTotal} parts (${percentage}%)`,
							];
						} // Intermediate stage
						const stageTotal = summaryStats.stageBreakdown[stage] || 0;
						const percentage =
							grandTotal > 0
								? ((stageTotal / grandTotal) * 100).toFixed(1)
								: "0.0";

						const lines = [
							`Total for ${stage}: ${stageTotal} parts (${percentage}%)`,
						];

						// SF (Special Release End date today or later) — only for
						// Quality and Pre-series Disposition stages.
						// QI breakdown only for Qualität stage
						const qiBreakdown =
							stage === "Qualität"
								? summaryStats.qiStageBreakdown?.[stage]
								: null;
						if (qiBreakdown) {
							const formatQi = (label, statusMap) => {
								const entries = Object.entries(statusMap || {})
									.filter(([k, v]) => v > 0 && k.toUpperCase().includes("NOK"))
									.sort((a, b) => b[1] - a[1]);
								if (entries.length === 0) return null;
								return `${label}: ${entries.map(([k, v]) => `${k}=${v}`).join(", ")}`;
							};
							lines.push("");
							lines.push("Quality Index Distribution");
							const q1 = formatQi("QI100", qiBreakdown.qi100);
							const q5 = formatQi("QI500", qiBreakdown.qi500);
							const q9 = formatQi("QI900", qiBreakdown.qi900);
							if (q1) lines.push(q1);
							if (q5) lines.push(q5);
							if (q9) lines.push(q9);
						}

						return lines;
					},
					// Special Release (SF) — rendered in the styled footer so it
					// stands out and stays consistently aligned below the body.
					// Only shown for Quality and Pre-series Disposition stages.
					footer: (context) => {
						if (!context || !Array.isArray(context) || context.length === 0) {
							return [];
						}
						const stage = context[0]?.label;
						if (stage !== "Qualität" && stage !== "Vorseriendisposition") {
							return [];
						}
						const sfCount = summaryStats.sfStageBreakdown?.[stage] || 0;
						return [`SF (Special Release ≥ today): ${sfCount}`];
					},
				},
			},
			title: {
				display: false,
			},
			datalabels: {
				display: function (context) {
					if (
						!context ||
						!context.dataset ||
						!context.chart ||
						!context.chart.data
					) {
						return false;
					}

					const datasetIndex = context.datasetIndex;
					const dataIndex = context.dataIndex;
					const stage = context.chart.data.labels?.[dataIndex];
					const value = context.dataset.data?.[dataIndex];

					// Don't show labels for base dataset
					if (context.dataset.label === "Base") {
						return false;
					}

					// For Neuteile gesamt and alle MST abgearbeitet, show individual stack labels if value > 0
					if (
						stage === "Neuteile gesamt" ||
						stage === "alle MST abgearbeitet"
					) {
						return value > 0;
					}

					// For intermediate stages, only show ONE label (from the first visible dataset with data)
					const datasets = context.chart.data.datasets;
					if (!datasets) return false;

					// Find the first dataset (excluding Base) that has data for this stage
					let firstDatasetWithData = -1;
					for (let i = 0; i < datasets.length; i++) {
						if (
							datasets[i]?.label !== "Base" &&
							datasets[i]?.data?.[dataIndex] >= 0
						) {
							firstDatasetWithData = i;
							break;
						}
					}

					// Only show label for the first dataset that has data (to avoid duplicates)
					return datasetIndex === firstDatasetWithData;
				},
				formatter: function (value, context) {
					if (!context || !context.chart || !context.chart.data) {
						return "";
					}

					const dataIndex = context.dataIndex;
					const stage = context.chart.data.labels?.[dataIndex];

					// For intermediate stages, show total instead of individual stack value
					if (
						stage !== "Neuteile gesamt" &&
						stage !== "alle MST abgearbeitet"
					) {
						// Calculate total for this stage
						const datasets = context.chart.data.datasets;
						if (!datasets) return "";

						let total = 0;
						datasets.forEach((dataset) => {
							if (
								dataset?.label !== "Base" &&
								dataset?.data?.[dataIndex] >= 0
							) {
								total += dataset.data[dataIndex];
							}
						});
						return total.toLocaleString(); // Show even if total is 0
					}

					// For Neuteile gesamt and alle MST abgearbeitet, show individual values
					return value > 0 ? value.toLocaleString() : "";
				},
				color: function (context) {
					// Use different colors for better visibility
					const value = context.dataset.data[context.dataIndex];
					const stage = context.chart.data.labels[context.dataIndex];

					// For zero values, use dark color for better visibility
					if (
						value === 0 &&
						stage !== "Neuteile gesamt" &&
						stage !== "alle MST abgearbeitet"
					) {
						return "white"; // Dark blue for zero values
					}
					return "white";
				},
				backgroundColor: function (context) {
					const value = context.dataset.data[context.dataIndex];
					const stage = context.chart.data.labels[context.dataIndex];

					// For zero values, use light background for better visibility
					if (
						value === 0 &&
						stage !== "Neuteile gesamt" &&
						stage !== "alle MST abgearbeitet"
					) {
						return "#353535"; // Light background for zero values
					}
					return "#353535";
				},
				font: {
					family: "MAN EUROPE_Regular",
					size: 14,
				},
				anchor: function (context) {
					const value = context.dataset.data[context.dataIndex];
					const stage = context.chart.data.labels[context.dataIndex];

					// For ALS stage (first bar after the red line), position labels
					// above the bar to avoid overlap with the red division line
					if (stage === "ALS") {
						return "end";
					}

					// For zero values in intermediate stages, position them higher
					if (
						value === 0 &&
						stage !== "Neuteile gesamt" &&
						stage !== "alle MST abgearbeitet"
					) {
						return "center";
					}
					return "center";
				},
				align: function (context) {
					const value = context.dataset.data[context.dataIndex];
					const stage = context.chart.data.labels[context.dataIndex];

					// For ALS stage (first bar after the red line), position labels
					// above the bar to avoid overlap with the red division line
					if (stage === "ALS") {
						return "top";
					}

					// For zero values in intermediate stages, position them higher
					if (
						value === 0 &&
						stage !== "Neuteile gesamt" &&
						stage !== "alle MST abgearbeitet"
					) {
						return 0; // Position 50px above the center
					}
					return "center";
				},
				offset: function (context) {
					const value = context.dataset.data[context.dataIndex];
					const stage = context.chart.data.labels[context.dataIndex];

					// For ALS stage (first bar after the red line), add 50px offset
					// upward to clear the red division line
					if (stage === "ALS") {
						return +50;
					}

					// For zero values in intermediate stages, add vertical offset to move them up
					if (
						value === 0 &&
						stage !== "Neuteile gesamt" &&
						stage !== "alle MST abgearbeitet"
					) {
						return -20; //
					}
					return 0;
				},
			},
		},
		scales: {
			x: {
				stacked: true,
				grid: {
					display: false,
					drawBorder: true,
					borderColor: "#cc0000",
					borderWidth: 2,
				},
				ticks: {
					font: {
						family: "MAN EUROPE_Bold",
						size: 16,
					},
					color: "#1e2832",
					maxRotation: 0,
					minRotation: 0,
					padding: 50,
					callback: function (value, index, ticks) {
						const label = this.getLabelForValue(value);
						return STAGE_DISPLAY_NAMES[label] || label;
					},
				},
			},
			y: {
				stacked: true,
				beginAtZero: true,
				afterDataLimits: function (scale) {
					// Add extra space above the chart to accommodate raised labels for Technik
					// Set minimum scale to ensure 50px equivalent space above highest bar
					const maxValue = scale.max;
					scale.max = maxValue * 1.15; // Add 15% padding for labels
				},
				grid: {
					color: "rgba(0, 0, 0, 0.1)",
					drawBorder: true,
					borderColor: "#cc0000",
					borderWidth: 2,
				},
				ticks: {
					font: {
						family: "MAN EUROPE_Regular",
						size: 11,
					},
					color: "#333",
				},
				title: {
					display: true,
					text: "Number of Parts",
					font: {
						family: "MAN EUROPE_Bold",
						size: 14,
					},
					color: "#1e2832",
				},
			},
		},
		interaction: {
			mode: "index",
			intersect: false,
		},
		elements: {
			bar: {
				borderRadius: 0,
				borderSkipped: false,
			},
		},
		onHover: (event, activeElements) => {
			if (event && event.native && event.native.target) {
				// In Floating mode bars aren't clickable, so keep the default cursor.
				if (floatingMode) {
					event.native.target.style.cursor = "default";
					return;
				}
				// Show pointer only when hovering over a real (non-Base) bar segment
				const chart = chartRef.current;
				let isOverRealBar = false;
				if (chart && activeElements && activeElements.length > 0) {
					isOverRealBar = activeElements.some((el) => {
						const lbl = chart.data.datasets[el.datasetIndex]?.label;
						return lbl && lbl !== "Base";
					});
				}
				event.native.target.style.cursor = isOverRealBar
					? "pointer"
					: "default";
			}
		},
		onClick: handleChartClick,
	};

	// Fetch data on component mount and when filters change
	useEffect(() => {
		fetchFilterOptions();
	}, [fetchFilterOptions]);

	useEffect(() => {
		if (!floatingMode) {
			fetchSeriesData();
		}
	}, [fetchSeriesData, floatingMode]);
	// Handle filter changes
	const handleFilterChange = (filterType, value) => {
		setSelectedFilters((prev) => {
			const newFilters = { ...prev, [filterType]: value };

			// Only cascade-reset downstream filters when the upstream change
			// actually invalidates them (i.e. a value was removed, not just added).
			// Adding a new project / ALG should never wipe out existing selections.

			if (filterType === "projectNumber") {
				// Keep only ALG values that are still valid under the new project set
				if (value.length > 0 && prev.alg.length > 0) {
					const validAlgs = new Set(
						value.flatMap(
							(proj) => filterOptions.projectAlgMapping?.[proj] || [],
						),
					);
					newFilters.alg = prev.alg.filter((a) => validAlgs.has(a));
				} else if (value.length === 0) {
					// All projects cleared — keep ALG as-is (user can still filter by ALG alone)
				}

				// Keep only change numbers still valid under the new project + remaining ALG set
				if (value.length > 0 && prev.changeNumber.length > 0) {
					const validChanges = new Set(
						value.flatMap(
							(proj) => filterOptions.projectChangeNoMapping?.[proj] || [],
						),
					);
					newFilters.changeNumber = prev.changeNumber.filter((cn) =>
						validChanges.has(cn),
					);
				} else if (value.length === 0) {
					// All projects cleared — keep change numbers as-is
				}
			}

			if (filterType === "alg") {
				// Keep only change numbers valid under the new ALG set
				if (value.length > 0 && prev.changeNumber.length > 0) {
					const validChanges = new Set(
						value.flatMap(
							(alg) => filterOptions.algChangeNoMapping?.[alg] || [],
						),
					);
					newFilters.changeNumber = prev.changeNumber.filter((cn) =>
						validChanges.has(cn),
					);
				} else if (value.length === 0) {
					// All ALGs cleared — keep change numbers as-is
				}
			}

			return newFilters;
		});
	};

	// Helper function to get active filters information for display.
	// In floating mode we surface the floating filter set (project + change no);
	// otherwise the series filter set (project + ALG + change no).
	const getActiveFiltersInfo = () => {
		const activeFilters = [];
		const source = floatingMode ? floatingAppliedFilters : appliedFilters;

		if (source.projectNumber && source.projectNumber.length > 0) {
			activeFilters.push({
				label: "Projects",
				count: source.projectNumber.length,
				values: source.projectNumber,
			});
		}

		if (!floatingMode && appliedFilters.alg && appliedFilters.alg.length > 0) {
			activeFilters.push({
				label: "ALG",
				count: appliedFilters.alg.length,
				values: appliedFilters.alg,
			});
		}

		if (floatingMode && source.alg && source.alg.length > 0) {
			activeFilters.push({
				label: "ALG",
				count: source.alg.length,
				values: source.alg,
			});
		}

		if (source.changeNumber && source.changeNumber.length > 0) {
			activeFilters.push({
				label: "Change No.",
				count: source.changeNumber.length,
				values: source.changeNumber,
			});
		}

		return activeFilters;
	};

	// ---- Floating Dashboard filter helpers ----

	// Update a floating filter; cascade-reset change numbers still valid under
	// the new project set (mirrors the main dashboard's behavior).
	const handleFloatingFilterChange = (filterType, value) => {
		setFloatingSelectedFilters((prev) => {
			const newFilters = { ...prev, [filterType]: value };

			if (filterType === "projectNumber") {
				// Keep only ALG values still valid under the new project set
				if (value.length > 0 && prev.alg.length > 0) {
					const validAlgs = new Set(
						value.flatMap(
							(proj) => floatingFilterOptions.projectAlgMapping?.[proj] || [],
						),
					);
					newFilters.alg = prev.alg.filter((a) => validAlgs.has(a));
				}
				// Keep only change numbers still valid under the new project set
				if (value.length > 0 && prev.changeNumber.length > 0) {
					const validChanges = new Set(
						value.flatMap(
							(proj) =>
								floatingFilterOptions.projectChangeNoMapping?.[proj] || [],
						),
					);
					newFilters.changeNumber = prev.changeNumber.filter((cn) =>
						validChanges.has(cn),
					);
				}
			}

			if (filterType === "alg") {
				// Keep only change numbers still valid under the new ALG set
				if (value.length > 0 && prev.changeNumber.length > 0) {
					const validChanges = new Set(
						value.flatMap(
							(a) => floatingFilterOptions.algChangeNoMapping?.[a] || [],
						),
					);
					newFilters.changeNumber = prev.changeNumber.filter((cn) =>
						validChanges.has(cn),
					);
				}
			}

			return newFilters;
		});
	};

	// Available ALG values scoped by the selected floating projects
	const getFloatingAvailableAlgValues = () => {
		const mapping = floatingFilterOptions.projectAlgMapping || {};
		let algValues;

		if (floatingSelectedFilters.projectNumber.length === 0) {
			algValues = floatingFilterOptions.algValues || [];
		} else {
			const vals = floatingSelectedFilters.projectNumber.reduce(
				(acc, project) => [...acc, ...(mapping[project] || [])],
				[],
			);
			algValues = [...new Set(vals)];
		}

		return algValues.sort((a, b) => {
			const numA = parseFloat(a);
			const numB = parseFloat(b);
			if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
			return a.toString().localeCompare(b.toString());
		});
	};

	// Available change numbers scoped by the selected floating projects AND ALGs
	const getFloatingAvailableChangeNumbers = () => {
		const projMapping = floatingFilterOptions.projectChangeNoMapping || {};
		const algMapping = floatingFilterOptions.algChangeNoMapping || {};
		const hasProject = floatingSelectedFilters.projectNumber.length > 0;
		const hasAlg = floatingSelectedFilters.alg.length > 0;
		let changeNumbers;

		if (!hasProject && !hasAlg) {
			changeNumbers = floatingFilterOptions.changeNumbers || [];
		} else if (hasProject && !hasAlg) {
			const nums = floatingSelectedFilters.projectNumber.reduce(
				(acc, project) => [...acc, ...(projMapping[project] || [])],
				[],
			);
			changeNumbers = [...new Set(nums)];
		} else if (!hasProject && hasAlg) {
			const nums = floatingSelectedFilters.alg.reduce(
				(acc, a) => [...acc, ...(algMapping[a] || [])],
				[],
			);
			changeNumbers = [...new Set(nums)];
		} else {
			// Both project and ALG selected — intersect the two candidate sets
			const byProject = new Set(
				floatingSelectedFilters.projectNumber.reduce(
					(acc, project) => [...acc, ...(projMapping[project] || [])],
					[],
				),
			);
			const byAlg = new Set(
				floatingSelectedFilters.alg.reduce(
					(acc, a) => [...acc, ...(algMapping[a] || [])],
					[],
				),
			);
			changeNumbers = [...byProject].filter((cn) => byAlg.has(cn));
		}

		return changeNumbers.sort((a, b) =>
			a.toString().localeCompare(b.toString()),
		);
	};

	// Format a "YYYY-MM-DD" snapshot date into "DD/MM/YYYY" for display,
	// matching the date style used elsewhere in the app. Falls back to the
	// raw string if it isn't in the expected shape.
	const formatShownDate = (isoDate) => {
		if (!isoDate || typeof isoDate !== "string") return "";
		const parts = isoDate.split("-");
		if (parts.length !== 3) return isoDate;
		const [y, m, d] = parts;
		return `${d}/${m}/${y}`;
	};

	const applyFloatingFilters = () => {
		setFloatingAppliedFilters({ ...floatingSelectedFilters });
	};

	const resetFloatingFilters = () => {
		setFloatingSelectedFilters({
			projectNumber: [],
			alg: [],
			changeNumber: [],
		});
		setFloatingAppliedFilters({ projectNumber: [], alg: [], changeNumber: [] });
	};

	const activeFiltersInfo = getActiveFiltersInfo();
	const totalActiveFilters = activeFiltersInfo.reduce(
		(sum, filter) => sum + filter.count,
		0,
	);

	// Apply filters
	const applyFilters = () => {
		setAppliedFilters({ ...selectedFilters });
	};

	// Reset filters
	const resetFilters = () => {
		setSelectedFilters({
			projectNumber: [],
			alg: [],
			changeNumber: [],
		});
		setAppliedFilters({
			projectNumber: [],
			alg: [],
			changeNumber: [],
		});
	};

	// Get available ALG values based on selected projects
	const getAvailableAlgValues = () => {
		let algValues;

		if (selectedFilters.projectNumber.length === 0) {
			algValues = filterOptions.algValues;
		} else {
			// Get ALG values for all selected projects
			const algValuesForSelectedProjects = selectedFilters.projectNumber.reduce(
				(acc, project) => {
					const projectAlgs = filterOptions.projectAlgMapping[project] || [];
					return [...acc, ...projectAlgs];
				},
				[],
			);

			// Remove duplicates
			algValues = [...new Set(algValuesForSelectedProjects)];
		}

		// Sort in ascending order
		return algValues.sort((a, b) => {
			const numA = parseFloat(a);
			const numB = parseFloat(b);
			if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
			return a.toString().localeCompare(b.toString());
		});
	};
	// Get available Change Numbers based on selected projects AND selected ALGs
	const getAvailableChangeNumbers = () => {
		let changeNumbers;

		const mapping = filterOptions.projectChangeNoMapping || {};
		const algChangeMapping = filterOptions.algChangeNoMapping || {};

		if (
			selectedFilters.projectNumber.length === 0 &&
			selectedFilters.alg.length === 0
		) {
			// No project or alg filter — show all
			changeNumbers = filterOptions.changeNumbers || [];
		} else if (
			selectedFilters.projectNumber.length > 0 &&
			selectedFilters.alg.length === 0
		) {
			// Filtered by project only
			const nums = selectedFilters.projectNumber.reduce((acc, project) => {
				return [...acc, ...(mapping[project] || [])];
			}, []);
			changeNumbers = [...new Set(nums)];
		} else if (
			selectedFilters.projectNumber.length === 0 &&
			selectedFilters.alg.length > 0
		) {
			// Filtered by ALG only
			const nums = selectedFilters.alg.reduce((acc, alg) => {
				return [...acc, ...(algChangeMapping[alg] || [])];
			}, []);
			changeNumbers = [...new Set(nums)];
		} else {
			// Both project and ALG selected — intersect
			const byProject = new Set(
				selectedFilters.projectNumber.reduce(
					(acc, project) => [...acc, ...(mapping[project] || [])],
					[],
				),
			);
			const byAlg = new Set(
				selectedFilters.alg.reduce(
					(acc, alg) => [...acc, ...(algChangeMapping[alg] || [])],
					[],
				),
			);
			changeNumbers = [...byProject].filter((cn) => byAlg.has(cn));
		}

		return changeNumbers.sort((a, b) =>
			a.toString().localeCompare(b.toString()),
		);
	};

	if (loading) {
		return (
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				height="60vh"
			>
				<CircularProgress size={60} thickness={4} sx={{ color: "#cc0000" }} />
				<Typography variant="body1" sx={{ mt: 2, color: "#666" }}>
					Loading series data...
				</Typography>
			</Box>
		);
	}
	if (error) {
		const hasAppliedFilters =
			appliedFilters.projectNumber.length > 0 ||
			appliedFilters.alg.length > 0 ||
			appliedFilters.changeNumber.length > 0;
		const isNoData = error === "No series data available";

		return (
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				height="60vh"
				gap={2}
			>
				<Alert
					severity={isNoData ? "info" : "error"}
					sx={{ maxWidth: 560, width: "100%" }}
				>
					{isNoData
						? "No data found for the selected filters."
						: `Error loading series data: ${error}`}
				</Alert>
				<Box display="flex" gap={1.5}>
					{!isNoData && (
						<Button
							variant="contained"
							onClick={fetchSeriesData}
							sx={{
								fontFamily: "MAN EUROPE_Bold",
								fontSize: "0.8rem",
								backgroundColor: "#cc0000",
								color: "white",
								"&:hover": { backgroundColor: "#aa0000" },
							}}
						>
							Retry
						</Button>
					)}
					{hasAppliedFilters && (
						<Button
							variant="outlined"
							onClick={resetFilters}
							sx={{
								fontFamily: "MAN EUROPE_Bold",
								fontSize: "0.8rem",
								borderColor: "#666",
								color: "#666",
								"&:hover": {
									borderColor: "#444",
									backgroundColor: "rgba(102,102,102,0.04)",
								},
							}}
						>
							Reset Filters
						</Button>
					)}
				</Box>
			</Box>
		);
	}
	return (
		<Box
			id="series-dashboard-container"
			sx={{
				backgroundColor: "#f0f0f0",
				minHeight: "100vh",
				width: "100%",
				maxWidth: "100%",
				overflow: "hidden",
			}}
		>
			{/* Summary Statistics Cards */}
			<Grid container spacing={3} sx={{ mb: 4 }}>
				{/* Top Row - Main Statistics */}
				<Grid item xs={12} md={4}>
					<Paper
						data-tour-target="series-dash-total-parts"
						elevation={2}
						sx={{ px: 3, py: 1.5, backgroundColor: "#fff", borderRadius: "8px" }}
					>
						<Typography
							variant="h6"
							sx={{ mb: 0.5, fontFamily: "MAN EUROPE_Bold" }}
						>
							Total Parts
						</Typography>
						<Typography
							variant="h4"
							sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832" }}
						>
							{summaryStats.totalParts.toLocaleString()}
						</Typography>
						<Typography variant="body2" sx={{ mt: 0.5, color: "#545454" }}>
							Total number of parts in series production
						</Typography>
					</Paper>
				</Grid>
				<Grid item xs={12} md={4}>
					<Paper
						data-tour-target="series-dash-workflow-stages"
						elevation={2}
						sx={{ px: 3, py: 1.5, backgroundColor: "#fff", borderRadius: "8px" }}
					>
						<Typography
							variant="h6"
							sx={{ mb: 0.5, fontFamily: "MAN EUROPE_Bold" }}
						>
							Workflow Stages
						</Typography>
						<Typography
							variant="h4"
							sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832" }}
						>
							{summaryStats.chartStages
								? summaryStats.chartStages.filter(
										(stage) =>
											stage !== "Neuteile gesamt" &&
											stage !== "alle MST abgearbeitet",
									).length
								: 0}
						</Typography>
						<Typography variant="body2" sx={{ mt: 1, color: "#545454" }}>
							Number of workflow stages in chart
						</Typography>
					</Paper>
				</Grid>
				<Grid item xs={12} md={4}>
					<Paper
						data-tour-target="series-dash-status-types"
						elevation={2}
						sx={{ px: 3, py: 1.5, backgroundColor: "#fff", borderRadius: "8px" }}
					>
						<Typography
							variant="h6"
							sx={{ mb: 0.5, fontFamily: "MAN EUROPE_Bold" }}
						>
							Status Types
						</Typography>
						<Typography
							variant="h4"
							sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832" }}
						>
							{Object.keys(summaryStats.statusBreakdown).length}
						</Typography>
						<Typography variant="body2" sx={{ mt: 0.5, color: "#545454" }}>
							Number of different status types
						</Typography>
					</Paper>
				</Grid>{" "}
			</Grid>

			{/* Main Waterfall Chart */}
			<Paper
				elevation={3}
				sx={{ p: 3, backgroundColor: "#fff", borderRadius: "8px", mb: 4 }}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						mb: 3,
					}}
				>
					<Box sx={{ flex: 1 }}>
						<Box
							sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
						>
							<Typography
								variant="h5"
								sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832" }}
							>
								{floatingMode
									? "Part progression in stages (Floating)"
									: "Part progression in stages"}
							</Typography>
							{floatingMode && (
								<MuiTooltip
									arrow
									placement="right"
									title={
										<Box sx={{ p: 0.5, maxWidth: 360 }}>
											<Typography
												sx={{
													fontFamily: "MAN EUROPE_Regular",
													fontSize: "0.75rem",
													lineHeight: 1.5,
												}}
											>
												In this dataset, each Ramp-up Group contains a given
												part number only once, ensuring uniqueness within that
												Ramp-up Group and supporting data quality. The same
												part number may appear under a different change number
												if applicable. This validation helps maintain data
												consistency and reliability.
											</Typography>
											<Typography
												sx={{
													fontFamily: "MAN EUROPE_Regular",
													fontSize: "0.75rem",
													lineHeight: 1.5,
													mt: 1,
												}}
											>
												For additional details, business rules, and answers to
												frequently asked questions, please refer to the:{" "}
												<a
													href="https://confluence.collaboration-man.com/spaces/MTBIMPM/pages/435775599/Floating+dashboard+Userguide"
													target="_blank"
													rel="noopener noreferrer"
													style={{
														color: "#ffffff",
														textDecoration: "underline"
													}}
												>
													Floating dashboard Userguide
												</a>
											</Typography>
										</Box>
									}
								>
									<InfoOutlinedIcon
										sx={{
											fontSize: "1.1rem",
											color: "#1e2832",
											cursor: "pointer",
										}}
									/>
								</MuiTooltip>
							)}
						</Box>

						{/* Floating snapshot day indicator. Shows which day's data is
						    displayed and flags when today's snapshot isn't available
						    yet (falls back to the most recent day). */}
						{floatingMode && floatingDataInfo.shownDate && (
							<Typography
								variant="body2"
								sx={{
									fontFamily: "MAN EUROPE_Regular",
									fontSize: "0.78rem",
									mb: 1,
									color: floatingDataInfo.isCurrentDay ? "#2e7d32" : "#b26a00",
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								{floatingDataInfo.isCurrentDay
									? `Data as of (${formatShownDate(
											floatingDataInfo.shownDate,
										)})`
									: `No data for today yet — showing data from ${formatShownDate(
											floatingDataInfo.shownDate,
										)}`}
							</Typography>
						)}

						{/* Active Filters Display */}
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-start",
								gap: 1,
								mb: 1,
								flexWrap: "wrap",
							}}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "flex-start",
									gap: 0.4,
									flexShrink: 0,
								}}
							>
								<Typography
									variant="body2"
									sx={{
										fontFamily: "MAN EUROPE_Regular",
										color: "#666",
										fontSize: "0.8rem",
									}}
								>
									Active Filters{" "}
									{totalActiveFilters > 0 && `(${totalActiveFilters})`}:
								</Typography>

								{/* Hide / Show PET toggle — only visible when ALG filter is active */}
								{appliedFilters.alg && appliedFilters.alg.length > 0 && (
									<Button
										size="small"
										variant="outlined"
										onClick={() => setShowPet((v) => !v)}
										sx={{
											fontFamily: "MAN EUROPE_Regular",
											fontSize: "0.6rem",
											height: "18px",
											padding: "0 6px",
											minWidth: "unset",
											borderColor: "#bbb",
											color: "#666",
											borderRadius: "3px",
											textTransform: "none",
											lineHeight: 1,
											"&:hover": {
												borderColor: "#1e2832",
												color: "#1e2832",
												backgroundColor: "rgba(30,40,50,0.04)",
											},
										}}
									>
										{showPet ? "Hide PET" : "Show PET"}
									</Button>
								)}
							</Box>
							{activeFiltersInfo.length > 0 ? (
								<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
									{activeFiltersInfo.map((filter, index) => {
										// For the ALG group, render each chip with its PET children below it
										const isAlg = filter.label === "ALG";
										if (
											isAlg &&
											appliedFilters.alg &&
											appliedFilters.alg.length > 0
										) {
											// Compute PET mapping (same logic as the standalone PET section)
											const pacMap =
												filterOptions.projectAlgChangePetMapping || {};
											const algPetMap = filterOptions.algPetMapping || {};
											const hasProject =
												appliedFilters.projectNumber &&
												appliedFilters.projectNumber.length > 0;
											const hasChange =
												appliedFilters.changeNumber &&
												appliedFilters.changeNumber.length > 0;

											const petsByAlg = {};
											filter.values.forEach((alg) => {
												const pets = new Set();
												if (hasProject) {
													appliedFilters.projectNumber.forEach((proj) => {
														const algMap = pacMap[proj]?.[alg] || {};
														if (hasChange) {
															appliedFilters.changeNumber.forEach((cn) => {
																(algMap[cn] || []).forEach((p) => pets.add(p));
															});
														} else {
															Object.values(algMap).forEach((arr) =>
																arr.forEach((p) => pets.add(p)),
															);
														}
													});
												} else if (hasChange) {
													Object.values(pacMap).forEach((projMap) => {
														const algMap = projMap[alg] || {};
														appliedFilters.changeNumber.forEach((cn) => {
															(algMap[cn] || []).forEach((p) => pets.add(p));
														});
													});
												} else {
													(algPetMap[alg] || []).forEach((p) => pets.add(p));
												}
												petsByAlg[alg] = [...pets].sort();
											});

											return (
												<Box
													key={index}
													sx={{
														display: "flex",
														flexDirection: "column",
														gap: 0.3,
													}}
												>
													{/* ALG label */}
													<Typography
														variant="caption"
														sx={{
															fontSize: "0.65rem",
															fontWeight: "bold",
															color: "#cc0000",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														{filter.label}:
													</Typography>

													{/* One column per ALG value, each with its PET children */}
													<Box
														sx={{
															display: "flex",
															gap: 1,
															flexWrap: "wrap",
															alignItems: "flex-start",
														}}
													>
														{filter.values.map((alg) => {
															const pets = petsByAlg[alg] || [];
															return (
																<Box
																	key={alg}
																	sx={{
																		display: "flex",
																		flexDirection: "column",
																		alignItems: "center",
																		gap: 0,
																	}}
																>
																	{/* ALG chip */}
																	<Chip
																		label={alg}
																		size="small"
																		sx={{
																			backgroundColor: "#cc0000",
																			color: "white",
																			fontSize: "0.65rem",
																			height: "18px",
																			fontFamily: "MAN EUROPE_Regular",
																			"& .MuiChip-label": { padding: "0 4px" },
																		}}
																	/>

																	{/* Connector: vertical line from ALG chip down to PET box */}
																	{showPet && (
																		<Box
																			sx={{
																				width: "1px",
																				height: "8px",
																				backgroundColor: "#bbb",
																				flexShrink: 0,
																			}}
																		/>
																	)}

																	{/* PET box — bordered container connected to the ALG above */}
																	{showPet && (
																		<Box
																			sx={{
																				border: "1px solid #bbb",
																				borderRadius: "3px",
																				px: "5px",
																				py: "2px",
																				display: "flex",
																				flexDirection: "column",
																				alignItems: "center",
																				gap: 0.3,
																				minWidth: "32px",
																			}}
																		>
																			<Typography
																				sx={{
																					fontFamily: "MAN EUROPE_Regular",
																					fontSize: "0.55rem",
																					color: "#888",
																					lineHeight: 1,
																					mb: "2px",
																					letterSpacing: "0.3px",
																				}}
																			>
																				PET
																			</Typography>
																			<Box
																				sx={{
																					display: "flex",
																					gap: 0.3,
																					flexWrap: "wrap",
																					justifyContent: "center",
																				}}
																			>
																				{pets.length > 0 ? (
																					pets.map((pet) => (
																						<Chip
																							key={pet}
																							label={pet}
																							size="small"
																							sx={{
																								backgroundColor: "#1e2832",
																								color: "white",
																								fontSize: "0.6rem",
																								height: "16px",
																								fontFamily:
																									"MAN EUROPE_Regular",
																								"& .MuiChip-label": {
																									padding: "0 4px",
																								},
																							}}
																						/>
																					))
																				) : (
																					<Typography
																						sx={{
																							fontFamily: "MAN EUROPE_Regular",
																							fontSize: "0.6rem",
																							color: "#bbb",
																							lineHeight: 1,
																						}}
																					>
																						—
																					</Typography>
																				)}
																			</Box>
																		</Box>
																	)}
																</Box>
															);
														})}
													</Box>
												</Box>
											);
										}

										// All other filter groups — unchanged flat chip style
										return (
											<Box
												key={index}
												sx={{
													display: "flex",
													flexDirection: "column",
													gap: 0.3,
												}}
											>
												<Typography
													variant="caption"
													sx={{
														fontSize: "0.65rem",
														fontWeight: "bold",
														color: "#cc0000",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													{filter.label}:
												</Typography>
												<Box
													sx={{ display: "flex", gap: 0.3, flexWrap: "wrap" }}
												>
													{filter.values.map((value, valueIndex) => (
														<Chip
															key={valueIndex}
															label={value}
															size="small"
															sx={{
																backgroundColor: "#cc0000",
																color: "white",
																fontSize: "0.65rem",
																height: "18px",
																fontFamily: "MAN EUROPE_Regular",
																"& .MuiChip-label": { padding: "0 4px" },
															}}
														/>
													))}
												</Box>
											</Box>
										);
									})}
								</Box>
							) : (
								<Typography
									variant="body2"
									sx={{
										color: "#999",
										fontSize: "0.75rem",
										fontFamily: "MAN EUROPE_Regular",
									}}
								>
									No active filters
								</Typography>
							)}
						</Box>
					</Box>

					<Box
						data-tour-target="series-dash-filters"
						sx={{
							display: "flex",
							gap: 1.5,
							alignItems: "center",
							flexWrap: "wrap",
							justifyContent: "flex-end",
						}}
					>
						<Typography
							variant="body2"
							sx={{
								fontFamily: "MAN EUROPE_Bold",
								color: "#1e2832",
								fontSize: "0.8rem",
								minWidth: "50px",
								flexShrink: 0,
							}}
						>
							Filters:
						</Typography>
						{!floatingMode && (
							<>
								<SearchableMultiSelect
									label="Projects"
									options={filterOptions.projectNumbers}
									value={selectedFilters.projectNumber}
									onChange={(val) => handleFilterChange("projectNumber", val)}
									minWidth={130}
								/>

								<SearchableMultiSelect
									label="ALG"
									options={getAvailableAlgValues()}
									value={selectedFilters.alg}
									onChange={(val) => handleFilterChange("alg", val)}
									minWidth={100}
								/>

								<SearchableMultiSelect
									label="Change No."
									options={getAvailableChangeNumbers()}
									value={selectedFilters.changeNumber}
									onChange={(val) => handleFilterChange("changeNumber", val)}
									minWidth={130}
								/>

								<Button
									size="small"
									variant="outlined"
									onClick={resetFilters}
									sx={{
										fontFamily: "MAN EUROPE_Bold",
										fontSize: "0.7rem",
										borderColor: "#666",
										color: "#666",
										minWidth: "55px",
										height: "28px",
										padding: "4px 8px",
										flexShrink: 0,
										"&:hover": {
											borderColor: "#444",
											backgroundColor: "rgba(102, 102, 102, 0.04)",
										},
									}}
								>
									Reset
								</Button>

								<Button
									size="small"
									variant="contained"
									onClick={applyFilters}
									sx={{
										fontFamily: "MAN EUROPE_Bold",
										fontSize: "0.7rem",
										backgroundColor: "#cc0000",
										color: "white",
										minWidth: "55px",
										height: "28px",
										padding: "4px 8px",
										flexShrink: 0,
										"&:hover": {
											backgroundColor: "#aa0000",
										},
									}}
								>
									Apply
								</Button>
							</>
						)}

						{floatingMode && (
							<>
								<SearchableMultiSelect
									label="Projects"
									options={floatingFilterOptions.projectNumbers}
									value={floatingSelectedFilters.projectNumber}
									onChange={(val) =>
										handleFloatingFilterChange("projectNumber", val)
									}
									minWidth={130}
								/>

								<SearchableMultiSelect
									label="ALG"
									options={getFloatingAvailableAlgValues()}
									value={floatingSelectedFilters.alg}
									onChange={(val) => handleFloatingFilterChange("alg", val)}
									minWidth={100}
								/>

								<SearchableMultiSelect
									label="Change No."
									options={getFloatingAvailableChangeNumbers()}
									value={floatingSelectedFilters.changeNumber}
									onChange={(val) =>
										handleFloatingFilterChange("changeNumber", val)
									}
									minWidth={130}
								/>

								<Button
									size="small"
									variant="outlined"
									onClick={resetFloatingFilters}
									sx={{
										fontFamily: "MAN EUROPE_Bold",
										fontSize: "0.7rem",
										borderColor: "#666",
										color: "#666",
										minWidth: "55px",
										height: "28px",
										padding: "4px 8px",
										flexShrink: 0,
										"&:hover": {
											borderColor: "#444",
											backgroundColor: "rgba(102, 102, 102, 0.04)",
										},
									}}
								>
									Reset
								</Button>

								<Button
									size="small"
									variant="contained"
									onClick={applyFloatingFilters}
									sx={{
										fontFamily: "MAN EUROPE_Bold",
										fontSize: "0.7rem",
										backgroundColor: "#cc0000",
										color: "white",
										minWidth: "55px",
										height: "28px",
										padding: "4px 8px",
										flexShrink: 0,
										"&:hover": {
											backgroundColor: "#aa0000",
										},
									}}
								>
									Apply
								</Button>

								{/* Opens the future-parts modal (floating_future_no_ident_dev
								    rows matching the current filter selection). */}
								<Button
									size="small"
									variant="outlined"
									onClick={openFutureModal}
									sx={{
										fontFamily: "MAN EUROPE_Bold",
										fontSize: "0.7rem",
										borderColor: "#1e2832",
										color: "#1e2832",
										height: "28px",
										padding: "4px 10px",
										flexShrink: 0,
										textTransform: "none",
										"&:hover": {
											borderColor: "#0f161d",
											backgroundColor: "rgba(30,40,50,0.04)",
										},
									}}
								>
									Future FET
								</Button>
							</>
						)}

						{/* Floating Dashboard on/off toggle — Development/local only.
						    isDevBranchEnvironment() is a RUNTIME check that reads
						    window.location.hostname in the browser, so the same built
						    bundle shows the button on the development host + localhost and
						    completely omits it from the DOM on the Production host — with
						    no rebuild needed and no disabled/hidden/empty placeholder. */}
						{isDevBranchEnvironment() && (
							<Button
								size="small"
								variant={floatingMode ? "contained" : "outlined"}
								onClick={() => setFloatingMode((v) => !v)}
								sx={{
									fontFamily: "MAN EUROPE_Bold",
									fontSize: "0.7rem",
									height: "28px",
									padding: "4px 10px",
									flexShrink: 0,
									textTransform: "none",
									...(floatingMode
										? {
												backgroundColor: "#1e2832",
												color: "white",
												"&:hover": { backgroundColor: "#0f161d" },
											}
										: {
												borderColor: "#1e2832",
												color: "#1e2832",
												"&:hover": {
													borderColor: "#0f161d",
													backgroundColor: "rgba(30,40,50,0.04)",
												},
											}),
								}}
							>
								{floatingMode ? "Floating: On" : "Floating: Off"}
							</Button>
						)}
					</Box>
				</Box>

				{chartData ? (
					<Box sx={{ height: "700px", width: "100%", position: "relative" }}>
						<Bar
							ref={chartRef}
							data={chartData}
							options={chartOptions}
							plugins={[
								redDivisionLinesPlugin,
								grandTotalLabelsPlugin,
								ChartDataLabels,
							]}
						/>
					</Box>
				) : (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						height="400px"
					>
						<Typography variant="body1" color="text.secondary">
							{floatingMode
								? "No floating dashboard data available for the selected filters"
								: "No series data available for chart"}
						</Typography>
					</Box>
				)}
			</Paper>

			{/* Future FET modal — rows from floating_future_no_ident_dev that
			    match the applied floating filters. Connected to the dashboard via
			    the shared project / ALG / change-number keys. */}
			<Dialog
				open={futureModalOpen}
				onClose={() => setFutureModalOpen(false)}
				maxWidth="lg"
				fullWidth
			>
				<DialogTitle
					sx={{
						fontFamily: "MAN EUROPE_Bold",
						color: "#1e2832",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						pr: 1,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<span>
							Future FET
							{!futureLoading && !futureError && ` (${futureData.length})`}
						</span>
						<MuiTooltip
							arrow
							placement="right"
							title={
								<Box sx={{ p: 0.5, maxWidth: 360 }}>
									<Typography
										sx={{
											fontFamily: "MAN EUROPE_Regular",
											fontSize: "0.75rem",
											lineHeight: 1.5,
										}}
									>
										This table contains the list of change numbers for the
										selected Project and ALG where no part numbers have been
										assigned yet, and the target PET date (Ziel PET) is later
										than the current date (Ziel PET &gt; Today). The list
										excludes change numbers with the deleted status "ST -
										Storniert".
									</Typography>
								</Box>
							}
						>
							<InfoOutlinedIcon
								sx={{
									fontSize: "1.1rem",
									color: "#1e2832",
									cursor: "pointer",
								}}
							/>
						</MuiTooltip>
					</Box>
					<IconButton
						onClick={() => setFutureModalOpen(false)}
						size="small"
						aria-label="close"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{futureInfo.shownDate && !futureLoading && !futureError && (
						<Typography
							variant="body2"
							sx={{
								fontFamily: "MAN EUROPE_Regular",
								fontSize: "0.78rem",
								mb: 1.5,
								color: futureInfo.isCurrentDay ? "#2e7d32" : "#b26a00",
							}}
						>
							{futureInfo.isCurrentDay
								? `Data as of (${formatShownDate(futureInfo.shownDate)})`
								: `No data for today yet — showing data from ${formatShownDate(
										futureInfo.shownDate,
									)}`}
						</Typography>
					)}

					{futureLoading ? (
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							py={5}
							gap={2}
						>
							<CircularProgress
								size={44}
								thickness={4}
								sx={{ color: "#cc0000" }}
							/>
							<Typography variant="body2" sx={{ color: "#666" }}>
								Loading Future FET...
							</Typography>
						</Box>
					) : futureError ? (
						<Alert severity="error">{futureError}</Alert>
					) : futureData.length === 0 ? (
						<Alert severity="info">
							No Future FET found for the selected filters.
						</Alert>
					) : (
						<TableContainer sx={{ maxHeight: "60vh" }}>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										{[
											["projektnummer", "Project"],
											["anlaufgruppe", "ALG"],
											["aenderungsnummer", "Change No."],
											["programm", "Programm"],
											["zielfet", "Ziel-FET"],
											["zielpet", "Ziel-PET"],
											["a80", "A80"],
										].map(([, label]) => (
											<TableCell
												key={label}
												sx={{
													fontFamily: "MAN EUROPE_Bold",
													fontSize: "0.72rem",
													backgroundColor: "#f0f4f8",
													color: "#1e2832",
													whiteSpace: "nowrap",
												}}
											>
												{label}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{futureData.map((row, idx) => (
										<TableRow key={idx} hover>
											{[
												"projektnummer",
												"anlaufgruppe",
												"aenderungsnummer",
												"programm",
												"zielfet",
												"zielpet",
												"a80",
											].map((col) => (
												<TableCell
													key={col}
													sx={{
														fontFamily: "MAN EUROPE_Regular",
														fontSize: "0.72rem",
														whiteSpace: "nowrap",
													}}
												>
													{row[col] === null ||
													row[col] === undefined ||
													row[col] === ""
														? "—"
														: String(row[col])}
												</TableCell>
											))}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default SeriesDashboard;
